import React, { PureComponent } from 'react';
import Button from '@mui/material/Button';
import { withStyles } from '@mui/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import CircularProgress from '@mui/material/CircularProgress';
import { ArrowUpward, ArrowDownward, FileCopy, Delete, Edit } from '@mui/icons-material';
import { grey } from '@mui/material/colors';
import CircuitModal from './CircuitModal';
import ChooseExercisesModal from './ChooseExercisesModal';
import { convertToMinuts } from './utilsSessionExercise';
import SumCell from './SumCell';
import TimeMatrixInput from './TimeMatrixInput';
import exerciseType from '../../../constants/exerciseType';

const style = () => ({
  root: {
    margin: '20px 0 40px 0',
  },
  header: {
    width: '100%',
  },
  title: {
    display: 'inline-block',
    width: '100px',
    marginRight: '20px',
  },
  sumCellTitle: {
    textAlign: 'right',
    backgroundColor: grey[300],
  },
  sumCellValue: {
    backgroundColor: grey[300],
  },
  loader: {
    width: '20px !important',
    height: '20px !important',
  },
  table: {
    backgroundColor: grey[300],
  },
  tableRow: {
    backgroundColor: grey[100],
  },
  tableCell: {
    padding: '4px 56px 4px 24px',
  },
  menuTableCell: {
    whiteSpace: 'nowrap',
  },
  circuitExerciseTableRow: {
    backgroundColor: grey[50],
  },
  circuitExerciseTableCell: {
    paddingLeft: '32px',
  },
  orderCell: {
    width: '40px',
    padding: '4px 8px',
  },
});

class SessionExercisesTable extends PureComponent {
  defaultCircuit = {
    name: '',
    restFor: 0,
  };

  constructor(props) {
    super(props);
    this.state = {
      openAddExercise: false,
      defaultPhaseDescription: !this.props.description,
      customPhaseDescription: this.props.description ?? '',
      showCircuitModal: false,
      circuit: this.getDefaultCircuit(),
    };
  }

  get durationKeys() {
    return Object.keys(this.props.durationOptions || {});
  }

  getDefaultCircuit() {
    return { ...this.defaultCircuit };
  }

  handleClickAddExercise = () => {
    this.setState({ openAddExercise: true });
  };

  handleCloseExercisesModal = () => {
    this.setState({ openAddExercise: false });
  };

  handleClickAddCircuit = () => {
    this.setState({
      showCircuitModal: true,
      circuit: this.getDefaultCircuit(),
    });
  };

  handleClickEditCircuit = (item) => {
    this.setState({
      showCircuitModal: true,
      circuit: { ...item },
    });
  };

  handleCloseCircuitModal = () => {
    this.setState({ showCircuitModal: false });
  };

  handleAction = (action, row, list, index) => {
    if (action && action.toLowerCase() === 'delete') {
      this.props.handleDelete(row);
    }
    if (action && action.toLowerCase() === 'move up') {
      this.props.handleUp(row, list, index);
    }
    if (action && action.toLowerCase() === 'move down') {
      this.props.handleDown(row, list, index);
    }
    if (action && action.toLowerCase() === 'duplicate') {
      this.props.handleDuplicate(row);
    }
    if (action && action.toLowerCase() === 'edit') {
      this.handleClickEditCircuit(row);
    }

    if (action && action.toLowerCase() === 'update_phase_description') {
      this.props.handleUpdatePhaseDescription(
        this.props.phase,
        this.state.defaultPhaseDescription ? undefined : this.state.customPhaseDescription,
      );
    }
  };

  getLastExer = () => {
    const exers = this.props.exercises;
    return exers[exers.length - 1].order;
  };

  getMaxOrder = (items) => {
    if (!items) {
      return 0;
    }
    return items
      .map((item) => item.order)
      .sort()
      .reduce((acc, cur) => {
        acc = cur > acc ? cur : acc;
        return acc;
      }, 0);
  };

  handleAddExercises = (event, circuitId) => {
    const durationOptions = this.durationKeys;
    const { phase } = this.props;

    const items = event.map((item) => {
      const timeMatrix = durationOptions.reduce((acc, current) => {
        // Set default value of 1 for lesson-type exercises
        acc[current] = item.type === 3 ? 1 : 0;
        return acc;
      }, {});

      return {
        circuitId: circuitId || null,
        exerciseId: item.id,
        phase,
        timeMatrix,
      };
    });

    this.props.addExercises(items);
  };

  renderTableHeader = () => {
    const { classes } = this.props;
    return (
      <TableHead>
        <TableRow>
          <TableCell className={classes.orderCell}>Order</TableCell>
          <TableCell>Exercises</TableCell>
          {this.durationKeys.map((item, i) => {
            return <TableCell key={i}>{item} Minutes</TableCell>;
          })}
          <TableCell>
            {this.props.loading ? <CircularProgress className={classes.loader} /> : null}
          </TableCell>
        </TableRow>
      </TableHead>
    );
  };

  renderTimeMatrixInput = (item, cell) => {
    const { loading, updateCell } = this.props;

    // Hide input for lesson-type exercises
    if (item.exercise?.type === exerciseType.lesson) {
      return <TextField disabled defaultValue="Lesson - video runtime" />;
    }

    let label = 'Seconds';

    if (item.type == 'ordinary') {
      label = exerciseType[item.exercise.type] + (item.exercise.forEachSide ? ' per side' : '');
    } else if (item.type == 'circuit') {
      label = 'Repeat';
    }
    return (
      <TimeMatrixInput
        label={label}
        value={item.timeMatrix[cell] || 0}
        disabled={loading}
        onCommit={(value) => updateCell(item, cell, value)}
      />
    );
  };

  renderCircuitExercisesRows = (item) => {
    if (item.type == 'circuit') {
      return item.sessionExercises.map((exercise, i) => {
        exercise.circuitId = item.id;
        return this.renderTableRow(exercise, i, item.sessionExercises);
      });
    }
    return null;
  };

  renderMenu = (item, pi, circuitId, exercises) => {
    const first = pi == 0;
    const last = pi == exercises.length - 1;
    const color = circuitId ? 'primary' : 'secondary';
    const showDuplicateButton = item.type == 'ordinary';
    const showEditButton = item.type == 'circuit';

    return (
      <>
        {showDuplicateButton && (
          <IconButton onClick={() => this.handleAction('duplicate', item)}>
            <FileCopy />
          </IconButton>
        )}
        {showEditButton && (
          <IconButton onClick={() => this.handleAction('edit', item)}>
            <Edit />
          </IconButton>
        )}
        <IconButton
          disabled={first}
          onClick={() => this.handleAction('move up', item, exercises, pi)}
        >
          <ArrowUpward color={first ? 'disabled' : color} />
        </IconButton>
        <IconButton
          disabled={last}
          onClick={() => this.handleAction('move down', item, exercises, pi)}
        >
          <ArrowDownward color={last ? 'disabled' : color} />
        </IconButton>
        <IconButton onClick={() => this.handleAction('delete', item, exercises, pi)}>
          <Delete />
        </IconButton>
      </>
    );
  };

  renderTableRow = (item, pi, exercises) => {
    const { classes } = this.props;
    const rowClass = item.circuitId ? classes.circuitExerciseTableRow : classes.tableRow;
    const cellClass = item.circuitId ? classes.circuitExerciseTableCell : classes.tableCell;
    let symbol = '➛';
    if (item.circuitId) {
      symbol = '⤷';
    }
    let suffix = '';

    if (item.type == 'circuit') {
      symbol = '↻';
      suffix = (
        <span>
          <br />
          Rest for: {item.restFor}
        </span>
      );
    }
    return (
      <TableRow key={item.id} classes={{ root: rowClass }}>
        <TableCell classes={{ root: `${cellClass} ${classes.orderCell}` }}>{item.order}</TableCell>
        <TableCell classes={{ root: cellClass }}>
          {symbol} {(item.exercise && item.exercise.name) || item.name} {suffix}
          <br />
          <small>{exerciseType[item.exercise?.type]}</small>
        </TableCell>
        {this.durationKeys.map((cell) => (
          <TableCell key={cell} classes={{ root: classes.tableCell }}>
            <FormControl>{this.renderTimeMatrixInput(item, cell)}</FormControl>
          </TableCell>
        ))}
        <TableCell classes={{ root: classes.menuTableCell }}>
          {this.renderMenu(item, pi, item.circuitId, exercises)}
        </TableCell>
      </TableRow>
    );
  };

  renderTableBody = () => {
    const { exercises } = this.props;
    return (
      <TableBody>
        {exercises.map((item, pi) => (
          <React.Fragment key={item.id}>
            {this.renderTableRow(item, pi, exercises)}
            {this.renderCircuitExercisesRows(item, pi)}
          </React.Fragment>
        ))}
      </TableBody>
    );
  };

  renderTableFooter = () => {
    const { classes, exercises, getSummaryDuration } = this.props;
    return (
      <TableBody>
        <TableRow>
          <TableCell className={classes.orderCell} />
          <TableCell className={classes.sumCellTitle}>TOTAL EXERCISE TIME</TableCell>
          {this.durationKeys.map((cell, i) => {
            const summaryDuration = getSummaryDuration(cell, exercises);
            return (
              <TableCell key={i} className={classes.sumCellValue}>
                <SumCell {...convertToMinuts(summaryDuration)} />
              </TableCell>
            );
          })}
          <TableCell className={classes.sumCellTitle} />
        </TableRow>
        <TableRow>
          <TableCell className={classes.orderCell} />
          <TableCell className={classes.sumCellTitle}>TOTAL TIME INCLUDING VIDEO</TableCell>
          {this.durationKeys.map((cell, i) => {
            const summaryDuration = getSummaryDuration(cell, exercises, true);
            return (
              <TableCell key={i} className={classes.sumCellValue}>
                <SumCell {...convertToMinuts(summaryDuration)} />
              </TableCell>
            );
          })}
          <TableCell className={classes.sumCellTitle} />
        </TableRow>
      </TableBody>
    );
  };

  renderTable = () => {
    const { classes } = this.props;
    const { exercises } = this.props;
    if (!exercises.length) {
      return null;
    }
    return (
      <Table classes={{ root: classes.table }}>
        {this.renderTableHeader()}
        {this.renderTableBody()}
        {this.renderTableFooter()}
      </Table>
    );
  };

  addCircuit = (values) => {
    const { addCircuit, exercises, phase } = this.props;

    values.order = this.getMaxOrder(exercises) + 1;
    values.phase = phase;

    addCircuit(values);
  };

  updateCircuit = (id, values) => {
    const { updateCircuit } = this.props;
    updateCircuit(id, values);
  };

  renderCircuitModal = () => {
    const { circuit } = this.state;
    if (!this.state.showCircuitModal) {
      return null;
    }
    return (
      <CircuitModal
        open={this.state.showCircuitModal}
        addCircuit={this.addCircuit}
        updateCircuit={this.updateCircuit}
        handleClose={this.handleCloseCircuitModal}
        circuit={circuit}
      />
    );
  };

  render() {
    const { title, classes, exercises } = this.props;
    const { openAddExercise } = this.state;

    const circuits = exercises.filter((item) => item.type == 'circuit');
    return (
      <div className={classes.root}>
        <Grid container direction="row" alignItems="center" style={{ marginBottom: 24 }}>
          <Grid
            container
            size={{ xs: 12 }}
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={1}
          >
            <Grid size={{ xs: 3 }}>
              <h4 className={classes.title}>{title}</h4>
            </Grid>
            <Grid
              container
              size={{ xs: 4 }}
              spacing={1}
              direction="row"
              alignItems="center"
              justifyContent="flex-end"
            >
              <Grid>
                <Button variant="outlined" color="primary" onClick={this.handleClickAddExercise}>
                  Add exercise
                </Button>
              </Grid>
              <Grid>
                <Button variant="outlined" color="primary" onClick={this.handleClickAddCircuit}>
                  Add circuit
                </Button>
              </Grid>
            </Grid>
          </Grid>
          <Grid
            container
            size={{ xs: 6 }}
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
          >
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Phase description"
                disabled={this.state.defaultPhaseDescription}
                defaultValue={this.state.customPhaseDescription}
                InputLabelProps={{
                  shrink: true,
                }}
                onChange={(e) =>
                  this.setState({
                    customPhaseDescription: e.target.value,
                  })
                }
                placeholder="Put your custom phase description here"
                fullWidth
                margin="normal"
              />
            </Grid>

            <Grid size={{ xs: 10 }}>
              <FormControlLabel
                control={
                  <Switch
                    name="checkedB"
                    color="primary"
                    checked={this.state.defaultPhaseDescription}
                    onClick={() => {
                      this.setState((prevState) => ({
                        defaultPhaseDescription: !prevState.defaultPhaseDescription,
                      }));
                    }}
                  />
                }
                label="Use default description"
              />
            </Grid>
            <Grid size={{ xs: 2 }}>
              <Button onClick={() => this.handleAction('update_phase_description', 'update')}>
                Update
              </Button>
            </Grid>
          </Grid>
        </Grid>
        {openAddExercise ? (
          <ChooseExercisesModal
            exercises={exercises}
            circuits={circuits}
            open={openAddExercise}
            handleClose={this.handleCloseExercisesModal}
            handleAddExercises={this.handleAddExercises}
          />
        ) : null}
        {this.renderCircuitModal()}
        {this.renderTable()}
      </div>
    );
  }
}

export default withStyles(style)(SessionExercisesTable);
